import { api } from '../lib/api';
import type {
  PaymentStatus,
  CreateQuickInvoiceRequest,
  CreateQuickInvoiceResponse,
  GeneratePaymentLinkResponse,
  MarkAsPaidResponse,
} from '../types/payments';

const CACHE_KEY_PREFIX = 'payment_status_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const PAYMENT_LINK_KEY_PREFIX = 'payment_link_';
const PAYMENT_LINK_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

// Simple cache for offline support
const paymentStatusCache = new Map<
  string,
  { data: PaymentStatus; timestamp: number }
>();

// Payment link storage utility
export const paymentLinkStorage = {
  // Save payment link for a dispatch
  savePaymentLink(dispatchId: string, paymentLink: string) {
    try {
      const key = `${PAYMENT_LINK_KEY_PREFIX}${dispatchId}`;
      localStorage.setItem(
        key,
        JSON.stringify({
          paymentLink,
          timestamp: Date.now(),
        })
      );
    } catch (e) {
      console.warn('Failed to save payment link to localStorage:', e);
    }
  },

  // Get payment link for a dispatch
  getPaymentLink(dispatchId: string): string | null {
    try {
      const key = `${PAYMENT_LINK_KEY_PREFIX}${dispatchId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Check if link is expired (older than 24 hours)
        if (Date.now() - parsed.timestamp < PAYMENT_LINK_EXPIRY) {
          return parsed.paymentLink;
        } else {
          // Clean up expired link
          localStorage.removeItem(key);
          console.log(
            `Payment link for dispatch ${dispatchId} expired and removed`
          );
        }
      }
    } catch (e) {
      console.warn('Failed to retrieve payment link from localStorage:', e);
    }
    return null;
  },

  // Clear payment link for a dispatch
  clearPaymentLink(dispatchId: string) {
    try {
      const key = `${PAYMENT_LINK_KEY_PREFIX}${dispatchId}`;
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Failed to clear payment link from localStorage:', e);
    }
  },
};

export const paymentApi = {
  async getPaymentStatus(
    dispatchId: string,
    companyId: string,
    token?: string
  ): Promise<PaymentStatus> {
    try {
      const headers: Record<string, string> = {
        'x-company-id': companyId,
      };

      // Add auth token if provided
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await api.get<PaymentStatus>(
        `/calls/${dispatchId}/payment`,
        {
          headers,
        }
      );

      // Cache the result
      paymentStatusCache.set(dispatchId, {
        data: response.data,
        timestamp: Date.now(),
      });

      // Also persist to localStorage for offline access
      try {
        localStorage.setItem(
          `${CACHE_KEY_PREFIX}${dispatchId}`,
          JSON.stringify({ data: response.data, timestamp: Date.now() })
        );
      } catch (e) {
        console.warn('Failed to cache payment status to localStorage:', e);
      }

      return response.data;
    } catch (error) {
      // Try to return cached data if available
      const cached = paymentStatusCache.get(dispatchId);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        console.log('Returning cached payment status');
        return cached.data;
      }

      // Try localStorage
      try {
        const stored = localStorage.getItem(`${CACHE_KEY_PREFIX}${dispatchId}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Date.now() - parsed.timestamp < CACHE_DURATION) {
            console.log('Returning localStorage cached payment status');
            return parsed.data;
          }
        }
      } catch (e) {
        console.warn('Failed to retrieve cached payment status:', e);
      }

      throw error;
    }
  },

  async createQuickInvoice(
    data: CreateQuickInvoiceRequest,
    companyId: string,
    token?: string
  ): Promise<CreateQuickInvoiceResponse> {
    const headers: Record<string, string> = {
      'x-company-id': companyId,
    };

    // Add auth token if provided
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await api.post<CreateQuickInvoiceResponse>(
      `/calls/${data.dispatchId}/invoice`,
      data,
      {
        headers,
      }
    );

    return response.data;
  },

  async generatePaymentLink(
    invoiceId: string,
    companyId: string,
    token?: string
  ): Promise<GeneratePaymentLinkResponse> {
    const headers: Record<string, string> = {
      'x-company-id': companyId,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await api.post<GeneratePaymentLinkResponse>(
      // The invoice route, for every app; it picks Square or RoadSync from
      // the company, where the driver app's copy only ever used Square.
      `/invoices/${invoiceId}/payment-link`,
      {},
      {
        headers,
      }
    );

    return response.data;
  },

  /**
   * Ask the payment provider for the invoice's latest status. The caller
   * refetches the call's payment summary afterwards; this only nudges the
   * backend to look.
   */
  async refreshPaymentStatus(
    invoiceId: string,
    companyId: string,
    token?: string
  ): Promise<unknown> {
    const headers: Record<string, string> = {
      'x-company-id': companyId,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await api.post(
      `/invoices/${invoiceId}/refresh-payment-status`,
      {},
      {
        headers,
      }
    );

    return response.data;
  },

  async markAsPaid(
    invoiceId: string,
    companyId: string,
    token?: string
  ): Promise<MarkAsPaidResponse> {
    const headers: Record<string, string> = {
      'x-company-id': companyId,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await api.put<MarkAsPaidResponse>(
      `/invoices/${invoiceId}/mark-as-paid`,
      {},
      {
        headers,
      }
    );

    return response.data;
  },

  // Clear cache for a dispatch
  clearCache(dispatchId: string) {
    paymentStatusCache.delete(dispatchId);
    try {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${dispatchId}`);
    } catch (e) {
      console.warn('Failed to clear payment cache:', e);
    }
  },
};
